<script setup>
  import {computed} from "vue";

  import ComponentTableRowCheckbox from "@components/Table/TableRowCheckbox.vue";
  import {usePage} from "@inertiajs/vue3";
  import PatientsPatientInformation from "@pages/Patients/Components/PatientInformation.vue";
  import {MedButton, MedTooltip} from "@pharma4u/medicheck-components";

  defineOptions({
    name: "PatientsDeletedPatientRow",
  });

  const props = defineProps({
    patient: {
      type: Object,
      required: true,
    },
    selectedRows: {
      type: Array,
      required: true,
    },
  });

  const emit = defineEmits(["confirm:delete", "confirm:recover", "update:selectedRows"]);

  const page = usePage();

  function confirmForceDelete() {
    emit("confirm:delete");
  }

  function confirmRecover() {
    emit("confirm:recover");
  }

  const isDeletable = computed(() => {
    return props.patient.from_wawi ? page.props.can.delete_wawi_patients : page.props.can.delete_patients;
  });
</script>

<template>
  <tr
    class="border-b-2 border-gray-200 leading-tight hover:bg-gray-100"
    :class="{'bg-gray-200': selectedRows.includes(patient.id)}"
    :data-test="getTestId('deleted-row')"
  >
    <!-- ☑️ multi-select & patient -->
    <td class="px-0.5 py-2">
      <MedTooltip v-if="patient.suffix || patient.prefix" content="Nameszusatz">
        <span class="text-sm" :class="{'ml-8': $page.props?.can?.allow_multi_select}">
          {{ patient.suffix }} {{ patient.prefix }}
        </span>
      </MedTooltip>

      <div class="flex items-center space-x-2">
        <ComponentTableRowCheckbox
          v-if="$page.props?.can?.allow_multi_select"
          :row-key="patient.id"
          :selected-rows="selectedRows"
          :test-id="getTestId('deleted-row')"
          @update:selected-rows="(newSelectedRows) => $emit('update:selectedRows', newSelectedRows)"
        />

        <div class="text-lg font-semibold text-gray-700">{{ patient.lastname }}, {{ patient.firstname }}</div>
      </div>

      <div class="flex items-center space-x-2" :class="{'ml-8': $page.props?.can?.allow_multi_select}">
        <MedTooltip content="Geburtsdatum">
          <span class="text-xs">
            {{ patient.birthdate_formatted }}
          </span>
        </MedTooltip>

        <MedTooltip content="Versichertennummer">
          <span class="text-xs">
            {{ patient.insurance_number }}
          </span>
        </MedTooltip>
      </div>
    </td>

    <!-- status -->
    <td class="px-0.5 py-2">
      <PatientsPatientInformation :patient="patient" />
    </td>

    <!-- last updates -->
    <td class="px-0.5 py-2">
      {{ patient.deleted_at }}
    </td>

    <!-- actions -->
    <td class="px-0.5 py-2">
      <div class="flex justify-center gap-4">
        <MedTooltip v-if="isDeletable" content="Wiederherstellen">
          <MedButton
            :aria-label="`Patient ${patient.lastname} wiederherstellen`"
            flat
            variant="primary"
            icon="sync"
            rounded="full"
            :data-test="getTestId('deleted-row-confirm-button')"
            @click="confirmRecover"
          />
        </MedTooltip>

        <MedTooltip v-if="isDeletable" content="Löschen">
          <MedButton
            :aria-label="`Patient ${patient.lastname} endgültig löschen`"
            flat
            icon="delete"
            rounded="full"
            :disabled="!isDeletable"
            :data-test="getTestId('deleted-row-delete-button')"
            @click="confirmForceDelete"
          />
        </MedTooltip>
      </div>
    </td>
  </tr>
</template>
